import React from 'react';
import _ from 'underscore';
import { Alert } from '@bingads-webui-react/alert';
import { isValidUrl } from '@bingads-webui/url-util';
import { Modal, Stack, Spinner, SpinnerSize, ISpinnerStyles } from '@fluentui/react';
import { setClarityTag } from '@bingads-webui-universal/clarity';
import { IImageExperienceImage } from '@bingads-webui-campaign-react/responsive-ad-editor-utils';
import { RecommendAdCarousel } from './recommend-ad-carousel';
import AdRecommendRepository from '../utils/ad-recommend-repository';
import { IActivityLogger, IRecommendAd, IAdRecommendResponse, ISetUpAdWithRecommendAdParam } from './types';
import { getRecommendCacheByUrl, setRecommendCache } from './recommend-ad-cache';
import { convertAdUnitsToImageExperiences } from '../utils/ad-recommend-util';

import './style.less';

declare let _TL_;

interface RecommendButtonProps {
  model: any;
  i18n: any;
  baseURL: string;
  setUpAdWithRecommendAd: (param: ISetUpAdWithRecommendAdParam) => void;
  activityLogger: IActivityLogger;
  shouldOverrideAdInEditor: boolean;
  isMMAV2Enabled: boolean;
}

interface IState {
  isOpen: boolean;
  url: string;
  loaded: boolean;
  ads: IRecommendAd[];
  imageExperiences: IImageExperienceImage[];
}

export class RecommendButton extends React.Component<RecommendButtonProps, IState> {
  constructor(props: RecommendButtonProps) {
    super(props);

    this.state = {
      isOpen: false,
      loaded: false,
      ads: [],
      imageExperiences: [],
      url: _.first(_.result(props.model.attributes, 'FinalUrls')),
    };
  }

  componentDidMount(): void {
    this.fetchRecommendation();
  }

  startToLoad = (url: string) => {
    if (url === this.state.url) {
      return;
    }
    this.setState({
      url,
      loaded: false,
    }, () => {
      this.fetchRecommendation();
    });
  };

  cleanData = () => {
    this.setState({
      ads: [],
      loaded: true,
    });
  };

  toggleOpen = (val: boolean) => {
    this.setState({
      isOpen: val,
    });
  };

  isRecommendAdinEditor = (res) => {
    const { model }= this.props;
    return _.any(res.value, item =>
      model.get('Headline0') === item.Headline &&
      model.get('Description0') === item.Text &&
      model.get('LongHeadline0') === item.LongHeadline &&
      model.get('Path1') === item.Path1 &&
      model.get('FinalUrls') === item.FinalUrls);
  };

  fetchRecommendation = async () => {
    const { baseURL } = this.props;
    const { url } = this.state;
    const repository = new AdRecommendRepository({ baseURL });

    if (isValidUrl(url)) {
      try {
        const resultFromCache = getRecommendCacheByUrl(url);
        let res: IAdRecommendResponse;
        if (resultFromCache) {
          res = resultFromCache;
        } else {
          res = await repository.getRecommendAds(url);
          this.props.activityLogger.signal(`fetch ad recommendation. The final URL: ${url}. The Response: ${JSON.stringify(res)}`, 'responsive ad recommendation');
          this.props.activityLogger.signal(`show recommendation button - recommend ad button. The final URL: ${url}.`, 'responsive ad recommendation');
          setRecommendCache(url, res);
        }
        // ad recommend preview for V2 image aspect ratio is 1.69, for V1 is 1.78
        const index = this.props.isMMAV2Enabled ? 4 : 1;
        const result = convertAdUnitsToImageExperiences(res.value, { imageListIndex: index }, this.props.isMMAV2Enabled);

        try {
          if (!_.isEmpty(res.value) && !this.isRecommendAdinEditor(res) && this.props.shouldOverrideAdInEditor) {
            await this.props.setUpAdWithRecommendAd({
              ad: res.value[0],
              shouldSaveAdFromRecommend: false,
            });
          }
          this.props.activityLogger.signal(`recommendation prefill sucessfully, The final URL: ${url}.`, 'responsive ad recommendation');
          setClarityTag({ AdRecommendPrefill: 'true' });
        } finally {
          this.setState({
            ads: res.value,
            loaded: true,
            imageExperiences: result,
          });
        }
      } catch (error) {
        this.setState({
          loaded: true,
        });
      }
    }
  };

  render() {
    const {
      i18n, setUpAdWithRecommendAd,
    } = this.props;

    const {
      isOpen, loaded, url, ads, imageExperiences,
    } = this.state;

    if (!isValidUrl(url)) {
      return null;
    }

    if (!loaded) {
      const spinnerStyles: ISpinnerStyles = { label: { color: 'black' } };
      return (
        <div style={{
          marginLeft: 16,
          marginTop: 10,
        }}
        >
          <Stack verticalFill horizontalAlign="start">
            <Spinner
              styles={spinnerStyles}
              size={SpinnerSize.small}
              labelPosition="right"
              label={i18n.getString(_TL_('Fetching recommendations...'))}
            />
          </Stack>
        </div>
      );
    }

    return ads.length === 0 ? null : (
      <div className="recommend-button" >
        <Alert
          action={{
              message: i18n.getString(_TL_('View recommendations')),
              callback: () => {
                this.toggleOpen(true);
                this.props.activityLogger.signal(`view recommendations - recommend ad button. The final URL: ${url}.`, 'responsive ad recommendation');
                setClarityTag({ AdRecommendationMSANModal: 'true' });
              },
            }}
          message={i18n.getString(_TL_('Ads recommended based on the Final URL.'))}
          level="Info"
          icon={(<div className="alert-image" />)}
          dismissible={false}
        />
        <Modal
          scrollableContentClassName="recommend-ad-modal"
          isOpen={isOpen}
          onDismiss={() => this.toggleOpen(false)}
          isBlocking={false}
        >
          <RecommendAdCarousel
            ads={ads}
            imageExperiences={imageExperiences}
            setUpAdWithRecommendAd={setUpAdWithRecommendAd}
            url={url}
            i18n={i18n}
            toggleOpen={this.toggleOpen}
            activityLogger={this.props.activityLogger}
            isMMAV2Enabled={this.props.isMMAV2Enabled}
          />
        </Modal>
      </div>
    );
  }
}


export function urlChangeForRecommendationAd(
  element,
  reactComponentRef: React.RefObject<RecommendButton>
) {
  const onUrlFocusout = (url: string) => {
    reactComponentRef.current && reactComponentRef.current.startToLoad(url);
  };

  const onUrlChanged = () => {
    reactComponentRef.current && reactComponentRef.current.cleanData();
  };

  element.on('urlChanged', onUrlChanged);
  element.on('urlFocusout', onUrlFocusout);
}
